import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const UserAdministrationPanel = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const users = [
    {
      id: "USR2025001",
      name: "Rajesh Kumar Sharma",
      email: "rajesh.sharma@example.com",
      phone: "+91 98765 43210",
      role: "Sugar Buyer",
      status: "active",
      kycStatus: "verified",
      registeredDate: "2025-10-15",
      lastActive: "2025-11-30",
      totalBookings: 45,
      totalSpent: 12500000
    },
    {
      id: "USR2025002",
      name: "Priya Patel",
      email: "priya.patel@example.com",
      phone: "+91 98765 43211",
      role: "Transporter",
      status: "active",
      kycStatus: "verified",
      registeredDate: "2025-09-20",
      lastActive: "2025-11-29",
      totalTrips: 120,
      totalEarnings: 3600000
    },
    {
      id: "USR2025003",
      name: "Amit Singh",
      email: "amit.singh@example.com",
      phone: "+91 98765 43212",
      role: "Factory Manager",
      status: "active",
      kycStatus: "pending",
      registeredDate: "2025-11-25",
      lastActive: "2025-11-28",
      totalLoads: 15,
      totalRevenue: 4500000
    },
    {
      id: "USR2025004",
      name: "Sunita Deshmukh",
      email: "sunita.d@example.com",
      phone: "+91 98765 43213",
      role: "Sugar Buyer",
      status: "suspended",
      kycStatus: "rejected",
      registeredDate: "2025-08-10",
      lastActive: "2025-11-20",
      suspensionReason: "Multiple payment disputes - under investigation",
      totalBookings: 8,
      totalSpent: 850000
    },
    {
      id: "USR2025005",
      name: "Vikram Reddy",
      email: "vikram.reddy@example.com",
      phone: "+91 98765 43214",
      role: "Transporter",
      status: "inactive",
      kycStatus: "verified",
      registeredDate: "2025-07-05",
      lastActive: "2025-10-15",
      totalTrips: 35,
      totalEarnings: 1050000
    }
  ];

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'Sugar Buyer', label: 'Sugar Buyer' },
    { value: 'Transporter', label: 'Transporter' },
    { value: 'Factory Manager', label: 'Factory Manager' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         user?.id?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesRole = roleFilter === 'all' || user?.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user?.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'inactive': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
      case 'suspended': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getKycStatusColor = (status) => {
    switch(status) {
      case 'verified': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getRoleIcon = (role) => {
    switch(role) {
      case 'Sugar Buyer': return 'ShoppingCart';
      case 'Transporter': return 'Truck';
      case 'Factory Manager': return 'Building2';
      default: return 'User';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    })?.format(amount);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Users" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">User Administration</h2>
            <p className="text-sm text-muted-foreground">Manage user accounts and permissions</p>
          </div>
        </div>
        <Button variant="default" iconName="UserPlus" iconPosition="left">
          Add New User
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-blue-700 dark:text-blue-400">Total Users</span>
            <Icon name="Users" size={20} className="text-blue-600 dark:text-blue-400" />
          </div>
          <p className="text-2xl font-bold text-blue-900 dark:text-blue-300">1,247</p>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">+12% from last month</p>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-green-700 dark:text-green-400">Active Users</span>
            <Icon name="UserCheck" size={20} className="text-green-600 dark:text-green-400" />
          </div>
          <p className="text-2xl font-bold text-green-900 dark:text-green-300">1,089</p>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">87.3% of total</p>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-yellow-700 dark:text-yellow-400">Pending KYC</span>
            <Icon name="Clock" size={20} className="text-yellow-600 dark:text-yellow-400" />
          </div>
          <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-300">45</p>
          <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">Requires review</p>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-red-700 dark:text-red-400">Suspended</span>
            <Icon name="UserX" size={20} className="text-red-600 dark:text-red-400" />
          </div>
          <p className="text-2xl font-bold text-red-900 dark:text-red-300">8</p>
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">Under investigation</p>
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Search by name, email, or user ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
          />
        </div>
        <Select
          options={roleOptions}
          value={roleFilter}
          onChange={setRoleFilter}
          placeholder="Filter by role"
          className="w-full md:w-48"
        />
        <Select
          options={statusOptions}
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="Filter by status"
          className="w-full md:w-48"
        />
        <Button variant="outline" iconName="Download" iconPosition="left">
          Export
        </Button>
      </div>
      <div className="space-y-3">
        {filteredUsers?.map((user) => (
          <div key={user?.id} className="border border-border rounded-lg p-4 hover:border-primary/50 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name={getRoleIcon(user?.role)} size={24} className="text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-foreground">{user?.name}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user?.status)}`}>
                      {user?.status?.toUpperCase()}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getKycStatusColor(user?.kycStatus)}`}>
                      KYC: {user?.kycStatus?.toUpperCase()}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm text-muted-foreground mb-2">
                    <span className="flex items-center gap-1">
                      <Icon name="Hash" size={14} />
                      {user?.id}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Mail" size={14} />
                      {user?.email}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Phone" size={14} />
                      {user?.phone}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Briefcase" size={14} />
                      {user?.role}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Calendar" size={14} />
                      Registered: {new Date(user.registeredDate)?.toLocaleDateString('en-IN')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Clock" size={14} />
                      Last Active: {new Date(user.lastActive)?.toLocaleDateString('en-IN')}
                    </span>
                  </div>
                  <div className="flex items-center gap-6 mt-2">
                    {user?.totalBookings !== undefined && (
                      <div>
                        <p className="text-xs text-muted-foreground">Total Bookings</p>
                        <p className="text-sm font-semibold text-foreground">{user?.totalBookings}</p>
                      </div>
                    )}
                    {user?.totalTrips !== undefined && (
                      <div>
                        <p className="text-xs text-muted-foreground">Total Trips</p>
                        <p className="text-sm font-semibold text-foreground">{user?.totalTrips}</p>
                      </div>
                    )}
                    {user?.totalLoads !== undefined && (
                      <div>
                        <p className="text-xs text-muted-foreground">Total Loads</p>
                        <p className="text-sm font-semibold text-foreground">{user?.totalLoads}</p>
                      </div>
                    )}
                    {user?.totalSpent !== undefined && (
                      <div>
                        <p className="text-xs text-muted-foreground">Total Spent</p>
                        <p className="text-sm font-semibold text-primary">{formatCurrency(user?.totalSpent)}</p>
                      </div>
                    )}
                    {user?.totalEarnings !== undefined && (
                      <div>
                        <p className="text-xs text-muted-foreground">Total Earnings</p>
                        <p className="text-sm font-semibold text-primary">{formatCurrency(user?.totalEarnings)}</p>
                      </div>
                    )}
                    {user?.totalRevenue !== undefined && (
                      <div>
                        <p className="text-xs text-muted-foreground">Total Revenue</p>
                        <p className="text-sm font-semibold text-primary">{formatCurrency(user?.totalRevenue)}</p>
                      </div>
                    )}
                  </div>
                  {user?.suspensionReason && (
                    <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                      <p className="text-sm font-medium text-red-800 dark:text-red-400 mb-1">Suspension Reason</p>
                      <p className="text-sm text-red-700 dark:text-red-300">{user?.suspensionReason}</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" iconName="Eye">
                  View
                </Button>
                <Button variant="outline" size="sm" iconName="Edit">
                  Edit
                </Button>
                {user?.status === 'suspended' ? (
                  <Button variant="success" size="sm" iconName="UserCheck">
                    Activate
                  </Button>
                ) : (
                  <Button variant="destructive" size="sm" iconName="UserX">
                    Suspend
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredUsers?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="UserSearch" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No users found matching your criteria</p>
        </div>
      )}
    </div>
  );
};

export default UserAdministrationPanel;